"use client";
import Container from "@/app/components/ui/container";
import Header from "@/app/components/ui/header";
import type { TenantKeyT } from "@/app/config/tenant-registry";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from "@/app/components/ui/select";
import { SelectValue } from "@radix-ui/react-select";
import Subheading from "@/app/components/ui/subheading";
import { useState } from "react";

// Types for select options and mapping
type TSelectValue =
  | "vw-da"
  | "audi-da"
  | "vwnfz-da"
  | "skoda-da"
  | "dreems-da"
  | "service-da"
  | "vw-ben"
  | "audi-ben"
  | "vwnfz-ben"
  | "skoda-ben"
  | "vw-gr"
  | "audi-gr"
  | "vwnfz-gr"
  | "skoda-gr";

type TSelectGroupKey = "darmstadt" | "bensheim" | "gross-umstadt";

type SelectOptionT = {
  value: TSelectValue;
  label: string;
};

type SelectGroupT = {
  key: TSelectGroupKey;
  label: string;
  options: SelectOptionT[];
};

type OnlineAppointmentClientPropsT = {
  tenantKey: TenantKeyT;
};

const IFRAME_URLS: Record<TSelectValue, string> = {
  "vw-da":
    "https://hrf-ota.hrf.de/9B9E7EF15BB947BDB44078CA181CF656/E723F06CCA454E81AA59119A14034845#service",
  "audi-da":
    "https://hrf-ota.hrf.de/9B9E7EF15BB947BDB44078CA181CF656/45DA917A0EB844D1A699C3C6B98D3F43#service",
  "skoda-da":
    "https://hrf-ota.hrf.de/9B9E7EF15BB947BDB44078CA181CF656/B19EF2B42DBA453D8342652F92377B2E#service",
  "vwnfz-da":
    "https://hrf-ota.hrf.de/9B9E7EF15BB947BDB44078CA181CF656/827E2DFD42AE41D681C900726369F95F#service",
  "dreems-da":
    "https://hrf-ota.hrf.de/9B9E7EF15BB947BDB44078CA181CF656/72D99F87602D4005BBADC2023327A039#service",
  "service-da":
    "https://hrf-ota.hrf.de/9B9E7EF15BB947BDB44078CA181CF656/C6CD332D18A44D0E8A32FE13654AEE3B#service",
  "vw-ben":
    "https://hrf-ota.hrf.de/9B9E7EF15BB947BDB44078CA181CF656/4CFDE6242C9949FF83F9BD9378BA1B43#service",
  "audi-ben":
    "https://hrf-ota.hrf.de/9B9E7EF15BB947BDB44078CA181CF656/4CFDE6242C9949FF83F9BD9378BA1B43#service",
  "skoda-ben":
    "https://hrf-ota.hrf.de/9B9E7EF15BB947BDB44078CA181CF656/4CFDE6242C9949FF83F9BD9378BA1B43#service",
  "vwnfz-ben":
    "https://hrf-ota.hrf.de/9B9E7EF15BB947BDB44078CA181CF656/4CFDE6242C9949FF83F9BD9378BA1B43#service",
  "vw-gr":
    "https://hrf-ota.hrf.de/ECFAF9086A854FC2A158C422A3C31B25/2B10185432394DD98D34968EFE7A688A#service",
  "audi-gr":
    "https://hrf-ota.hrf.de/ECFAF9086A854FC2A158C422A3C31B25/A5FF53F6F11F4E4DA61675B95694EEE8#service",
  "skoda-gr":
    "https://hrf-ota.hrf.de/ECFAF9086A854FC2A158C422A3C31B25/7243A9D7EF954C32BB2FB84D396F8F0B#service",
  "vwnfz-gr":
    "https://hrf-ota.hrf.de/ECFAF9086A854FC2A158C422A3C31B25/37762191B31D47FDBE43D51BCDA27D00#service",
};

const SCHUETZ_SELECT_GROUP_KEY: TSelectGroupKey = "gross-umstadt";

// Options grouped by location for rendering.
const SELECT_GROUPS: SelectGroupT[] = [
  {
    key: "darmstadt",
    label: "Darmstadt",
    options: [
      { value: "vw-da", label: "Volkswagen" },
      { value: "audi-da", label: "Audi" },
      { value: "vwnfz-da", label: "Volkswagen Nutzfahrzeuge" },
      { value: "skoda-da", label: "Skoda" },
      { value: "dreems-da", label: "Dreems" },
      { value: "service-da", label: "Großkunden" },
    ],
  },
  {
    key: "bensheim",
    label: "Bensheim",
    options: [
      { value: "vw-ben", label: "Volkswagen" },
      { value: "audi-ben", label: "Audi" },
      { value: "vwnfz-ben", label: "Volkswagen Nutzfahrzeuge" },
      { value: "skoda-ben", label: "Skoda" },
    ],
  },
  {
    key: "gross-umstadt",
    label: "Groß-Umstadt",
    options: [
      { value: "vw-gr", label: "Volkswagen" },
      { value: "audi-gr", label: "Audi" },
      { value: "vwnfz-gr", label: "Volkswagen Nutzfahrzeuge" },
      { value: "skoda-gr", label: "Skoda" },
    ],
  },
];

const getSelectGroupsForTenant = (tenantKey: TenantKeyT): SelectGroupT[] => {
  if (tenantKey !== "schuetz-gross-umstadt") {
    return SELECT_GROUPS;
  }

  const schuetzGroup = SELECT_GROUPS.find(
    (group) => group.key === SCHUETZ_SELECT_GROUP_KEY
  );

  return schuetzGroup ? [schuetzGroup] : SELECT_GROUPS;
};

export default function OnlineAppointmentClient({
  tenantKey,
}: OnlineAppointmentClientPropsT) {
  const isSchuetzTenant = tenantKey === "schuetz-gross-umstadt";
  const selectGroups = getSelectGroupsForTenant(tenantKey);

  // State for selected value and iframe URL
  const [selected, setSelected] = useState<TSelectValue | "">("");
  const iframeUrl = selected ? IFRAME_URLS[selected as TSelectValue] : "";
  const iframeHeight = selected ? 1100 : 0;

  return (
    <>
      <Header title="Online-Terminvereinbarung" />
      <Container>
        <section className="py-8 md:py-12">
          <Subheading>Termin vereinbaren</Subheading>
          <h2 className="heading-large">Online-Terminvereinbarung</h2>
          <p className="pt-2">
            Seit drei Jahren bieten wir Ihnen die Möglichkeit, den
            Räderwechseltermin selbst online zu buchen. Wir haben damit gute
            Erfahrungen gemacht und wollen Ihnen zukünftig auch die Gelegenheit
            bieten, nicht nur den Räderwechsel, sondern auch weitere
            Reparaturen, Servicetermine etc. bei uns ganzjährig online
            vereinbaren zu können.
          </p>

          <div className="mt-12">
            <h3 className="heading-medium">
              {isSchuetzTenant
                ? "Wählen Sie die Marke aus:"
                : "Wählen Sie die Marke und den Standort aus:"}
            </h3>
            <div className="mt-4 mb-6 font-medium">
              {isSchuetzTenant ? "Marke wählen:" : "Marke & Standort wählen:"}
            </div>
            {/* Select with grouped options */}
            <Select
              value={selected}
              onValueChange={(value) => setSelected(value as TSelectValue | "")}
            >
              <SelectTrigger className="bg-background w-[220px] truncate py-6 shadow-sm">
                <SelectValue placeholder="Bitte auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {selectGroups.map((group, idx) => (
                  <div
                    key={group.key}
                    className={idx < selectGroups.length - 1 ? "mb-2" : ""}
                  >
                    <SelectGroup>
                      {!isSchuetzTenant ? (
                        <SelectLabel className="bg-muted rounded-md">
                          {group.label}
                        </SelectLabel>
                      ) : null}
                      {group.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* iFrame for service booking, only visible if a selection is made */}
          <div className="mt-8">
            {iframeUrl && (
              <iframe
                name="service-iframe"
                src={iframeUrl}
                title="Service-Terminvereinbarung"
                className="w-full rounded border"
                style={{
                  height: `${iframeHeight}px`,
                  transition: "height 0.3s",
                }}
                loading="lazy"
                allow="clipboard-write"
              />
            )}
          </div>
        </section>
      </Container>
    </>
  );
}
